import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import { PanelBody, SelectControl, ComboboxControl, TextControl, Placeholder, Spinner } from '@wordpress/components';
import { useState, useEffect } from '@wordpress/element';
import useStyles from '../../js/hooks/useStyles';
import { casFetch } from '../../js/casFetch';

export default function Edit({ attributes, setAttributes }) {
    const { projectOfferId, style, ctaText } = attributes;
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Fetch available styles for offer blocks
    const { styles: styleOptions, loading: stylesLoading } = useStyles('offer');

    // Fetch project offers with full data for preview
    useEffect(() => {
        casFetch('/wp-json/cas/v1/project-offers')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch offers');
                }
                return response.json();
            })
            .then(data => {
                if (data.success && Array.isArray(data.data)) {
                    setOffers(data.data);
                } else {
                    setError('No offers available');
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, []);

    // Get the selected offer data for preview
    const selectedOffer = offers.find(offer => offer.uuid === projectOfferId);

    const blockProps = useBlockProps({
        className: 'wp-block-cas-project-offer'
    });

    // Every branch below returns the same shape: the canvas div, then the
    // inspector. React reconciles a component by the type of the element it
    // returns, so returning a bare <div> while loading and a <> once loaded
    // tears the whole block down and rebuilds it the instant the fetch
    // resolves. Anything the editor had already measured against the old
    // node is then left holding a detached one, which surfaces inside
    // @wordpress/components as getComputedStyle receiving a non-Element.
    // It only bites when the request lands after the editor's post-insert
    // work - fast locally, slow on a remote site - which is why this
    // reproduced on staging and not in development.
    const styleInspector = (
        <InspectorControls>
            <PanelBody title={__('Offer Settings', 'cas-plugin')} className="cas-block-inspector">
                <SelectControl
                    label={__('Style', 'cas-plugin')}
                    value={style}
                    options={styleOptions}
                    onChange={(value) => setAttributes({ style: value })}
                    disabled={stylesLoading}
                    help={stylesLoading ? __('Loading available styles...', 'cas-plugin') : __('Choose a style template for this offer.', 'cas-plugin')}
                />
            </PanelBody>
        </InspectorControls>
    );

    if (loading) {
        return (
            <>
                <div {...blockProps}>
                    <Placeholder 
                        icon="star-filled" 
                        label={__('CAS Project Offer', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <Spinner />
                        <p>{__('Loading offers...', 'cas-plugin')}</p>
                    </Placeholder>
                </div>
                {styleInspector}
            </>
        );
    }

    if (error) {
        return (
            <>
                <div {...blockProps}>
                    <Placeholder 
                        icon="warning" 
                        label={__('CAS Project Offer', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <p>{__('Error loading offers: ', 'cas-plugin') + error}</p>
                    </Placeholder>
                </div>
                {styleInspector}
            </>
        );
    }

    if (!projectOfferId) {
        return (
            <>
                <div {...blockProps}>
                    <Placeholder 
                        icon="star-filled" 
                        label={__('CAS Project Offer', 'cas-plugin')}
                        instructions={__('Search and select a project offer to display', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <ComboboxControl
                            label={__('Search Project Offers', 'cas-plugin')}
                            hideLabelFromVision
                            value={projectOfferId || null}
                            options={offers.map(offer => ({
                                label: `${offer.name || 'Unnamed Offer'}${offer.brand_name ? ` (${offer.brand_name})` : ''}`,
                                value: offer.uuid
                            }))}
                            onChange={(value) => {
                                setAttributes({
                                    projectOfferId: value
                                });
                            }}
                            placeholder={__('Type to search offers...', 'cas-plugin')}
                        />
                    </Placeholder>
                </div>
                {styleInspector}
            </>
        );
    }

    // Client-side preview - no more server requests!
    return (
        <>
            <div {...blockProps}>
                {selectedOffer ? (
                    <div className="cas-offer-container">
                        <div className="cas-toplist-header">
                            <div className="cas-offer-name">
                                {selectedOffer.name || 'Casino Offer'}
                            </div>
                        </div>
                        <div className="cas-offer-skeleton">
                            <div 
                                className="cas-logo-placeholder"
                                style={{ 
                                    backgroundColor: selectedOffer.brand_logo_background_color || '#f8f9fa'
                                }}
                            >
                                {selectedOffer.brand_logo_url ? (
                                    <img 
                                        src={selectedOffer.brand_logo_url} 
                                        alt={selectedOffer.brand_name || 'Brand Logo'}
                                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <span style={{ fontSize: '10px', color: '#6c757d' }}>LOGO</span>
                                )}
                            </div>
                            <div className="cas-offer-content">
                                <div 
                                    className="cas-offer-title"
                                    dangerouslySetInnerHTML={{
                                        __html: selectedOffer.offer_title_highlighted || selectedOffer.offer_title || selectedOffer.name || 'Special Offer'
                                    }}
                                />
                                {selectedOffer.offer_subtitle && (
                                    <div className="cas-offer-subtitle">
                                        {selectedOffer.offer_subtitle}
                                    </div>
                                )}
                            </div>
                            <div className="cas-cta-placeholder">
                                {ctaText || selectedOffer.cta_text || 'Get Offer'}
                            </div>
                            <div 
                                className="cas-offer-terms"
                                dangerouslySetInnerHTML={{
                                    __html: selectedOffer.offer_terms || 'T&Cs apply. 18+. Gamble responsibly.'
                                }}
                            />
                        </div>
                    </div>
                ) : (
                    <Placeholder 
                        icon="warning" 
                        label={__('CAS Project Offer', 'cas-plugin')}
                        className="cas-block-placeholder"
                    >
                        <p>{__('Selected offer not found. Please choose a different offer.', 'cas-plugin')}</p>
                    </Placeholder>
                )}
            </div>
            <InspectorControls>
                <PanelBody title={__('Offer Settings', 'cas-plugin')} className="cas-block-inspector">
                    <ComboboxControl
                        label={__('Search Project Offers', 'cas-plugin')}
                        value={projectOfferId || null}
                        options={offers.map(offer => ({
                            label: `${offer.name || 'Unnamed Offer'}${offer.brand_name ? ` (${offer.brand_name})` : ''}`,
                            value: offer.uuid
                        }))}
                        onChange={(value) => {
                            setAttributes({
                                projectOfferId: value
                            });
                        }}
                        allowReset={true}
                        placeholder={__('Type to search offers...', 'cas-plugin')}
                        help={__('Search by offer name or brand to quickly find the offer you want to display.', 'cas-plugin')}
                    />
                    <SelectControl
                        label={__('Style', 'cas-plugin')}
                        value={style}
                        options={styleOptions}
                        onChange={(value) => setAttributes({ style: value })}
                        disabled={stylesLoading}
                        help={stylesLoading ? __('Loading available styles...', 'cas-plugin') : __('Choose a style template for this offer.', 'cas-plugin')}
                    />
                    <TextControl
                        label={__('Custom CTA Text', 'cas-plugin')}
                        value={ctaText}
                        onChange={(value) => setAttributes({ ctaText: value })}
                        placeholder={__('Leave empty to use default', 'cas-plugin')}
                        help={__('Override the call-to-action button text. Leave empty to use the default text from the offer settings.', 'cas-plugin')}
                    />
                </PanelBody>
            </InspectorControls>
        </>
    );
}
